import {
    Logger as logger4js,
    logLog,
    getLogger,
    AjaxAppender,
    JsonLayout,
    BrowserConsoleAppender,
} from "log4javascript";
import uuid from "uuid";
import { browserName, fullBrowserVersion } from "react-device-detect";
import { getGisServerUrl } from "../../api/gisServerUrl";

logLog.setQuietMode(true);

const BATCH_SIZE = 20;

class Logger {
    private readonly identifier: string;
    logger: logger4js;
    loggerName: string;

    constructor(loggerName?: string) {
        this.logger = getLogger();
        this.loggerName = loggerName || "anonymous";

        this.identifier = uuid();

        this._addAppenders();
    }

    async _createAjaxAppender(): Promise<AjaxAppender> {
        const gisServerUrl = await getGisServerUrl();

        const ajaxAppender = new AjaxAppender(`${gisServerUrl}rest/log/logJson`);

        ajaxAppender.setBatchSize(BATCH_SIZE);
        ajaxAppender.setSendAllOnUnload(true);
        const layout = new JsonLayout(false, false);

        layout.setCustomField("identifier", this.identifier);

        ajaxAppender.setLayout(layout);
        return ajaxAppender;
    }

    async _addAppenders() {
        const appenders = this.logger.getEffectiveAppenders();
        if (!appenders || appenders.length <= 0) {
            this.logger.addAppender(await this._createAjaxAppender());
            this.logger.addAppender(new BrowserConsoleAppender());
            this.info(
                `Creating logger in browser: ${browserName} version: ${fullBrowserVersion} with id: ${this.identifier}`
            );
        }
    }

    _getFileLineAndColumn() {
        const stack = new Error().stack;
        const callerLine = stack?.split("\n")[3];
        const fileLineAndColumn = callerLine?.match(/([\w-_.]+\.(js|jsx|ts|tsx)):(\d*):(\d*)/);
        return fileLineAndColumn ? fileLineAndColumn[0] : callerLine;
    }

    _getExceptionText(exception?: Error) {
        return exception ? exception.stack || exception.message || exception : "";
    }

    error(message: string, exception?: Error) {
        const exceptionText = this._getExceptionText(exception);
        this.logger.error(
            this.loggerName,
            this._getFileLineAndColumn(),
            message,
            exceptionText ? "\n" + exceptionText : ""
        );
    }

    warn(message: string) {
        this.logger.warn(this.loggerName, this._getFileLineAndColumn(), message);
    }

    info(message: string) {
        this.logger.info(this.loggerName, this._getFileLineAndColumn(), message);
    }

    sendAllCachedMessages() {
        for (let i = 0; i < BATCH_SIZE; i++) {
            this.logger.info();
        }

        (this.logger.getEffectiveAppenders()[0] as AjaxAppender).sendAll();
    }
}

window.logger = new Logger();

window.onerror = (msg, url, lineNo, columnNo, error) => {
    window.logger.error(msg, error);
    window.logger.sendAllCachedMessages();
};

export default Logger;
